import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { LinearProgress, Tooltip } from '@material-ui/core';
import { EmojiEvents, ChevronRight } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { TournamentType } from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { TournamentResultsData, TournamentResultsQuery, TournamentResultsVars, TournamentResultView } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { classImages } from 'utilities/ClassImages';
import { PageEmpty, PAGE_KIT } from 'components/Page/PageKit';
import { Segmented } from '../Pve/run/PveRunList';
import { TYPE_COLORS, TOURNAMENT_ACCENT, bracketLabel, typeLabel } from './tournamentFormat';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridRowGap: theme.spacing(2),
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignItems: 'center',
    gridColumnGap: 12,
  },
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gridGap: 10,
  },
  stat: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  statHero: {
    background: 'radial-gradient(circle at 100% 0%, rgba(255, 179, 0, 0.22), transparent 60%), rgba(255, 255, 255, 0.035)',
    borderColor: 'rgba(255, 179, 0, 0.4)',
  },
  statLabel: {
    fontSize: '0.66rem',
    fontWeight: 900,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.muted,
  },
  statValue: {
    marginTop: 2,
    fontSize: '1.45rem',
    fontWeight: 900,
    fontVariantNumeric: 'tabular-nums',
  },
  statNote: {
    fontSize: '0.72rem',
    color: PAGE_KIT.faint,
  },
  list: {
    display: 'grid',
    gridRowGap: 8,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '48px minmax(0, 1fr) auto 20px',
    gridColumnGap: 14,
    alignItems: 'center',
    padding: theme.spacing(1.1, 1.5),
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    cursor: 'pointer',
    transition: 'border-color 0.12s, transform 0.12s',
    '&:hover': {
      borderColor: 'rgba(255, 179, 0, 0.5)',
      transform: 'translateY(-1px)',
    },
  },
  place: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    alignContent: 'center',
    lineHeight: 1,
    fontWeight: 900,
    fontSize: '1.05rem',
    background: 'rgba(255, 255, 255, 0.06)',
  },
  placeOf: {
    marginTop: 3,
    fontSize: '0.6rem',
    fontWeight: 800,
    opacity: 0.7,
  },
  name: {
    fontWeight: 900,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  meta: {
    marginTop: 3,
    fontSize: '0.76rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  typeTag: {
    display: 'inline-block',
    marginRight: 8,
    padding: '0 7px',
    borderRadius: 7,
    fontSize: '0.62rem',
    fontWeight: 900,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  flag: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '0 6px',
    borderRadius: 6,
    fontSize: '0.62rem',
    fontWeight: 800,
    color: PAGE_KIT.muted,
    background: 'rgba(255, 255, 255, 0.07)',
  },
  record: {
    textAlign: 'right',
  },
  recordMain: {
    fontWeight: 900,
    fontVariantNumeric: 'tabular-nums',
  },
  icons: {
    marginTop: 4,
    textAlign: 'right',
    '& img': {
      width: 20,
      height: 20,
      borderRadius: '50%',
      marginLeft: 3,
      verticalAlign: 'middle',
    },
  },
  go: {
    color: PAGE_KIT.faint,
  },
}));

/** medal colours for the podium */
const PLACE_STYLE: Record<number, {color: string, background: string}> = {
  1: {color: '#1a1300', background: `linear-gradient(135deg, #ffe082, ${TOURNAMENT_ACCENT})`},
  2: {color: '#15181c', background: 'linear-gradient(135deg, #eceff1, #90a4ae)'},
  3: {color: '#1c120b', background: 'linear-gradient(135deg, #ffcc80, #b26a3c)'},
};

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

type TypeFilter = 'all' | TournamentType;

interface Props {
  userId?: string;
  characterId?: string;
}

/**
 * A player's (or a character's) finished tournaments: totals, then each tournament with where they finished. Shared by
 * the app and the website profile pages.
 */
export const TournamentHistory: React.FC<Props> = ({userId, characterId}) => {
  const classes = useStyles();
  const history = useHistory();
  const [type, setType] = useState<TypeFilter>('all');
  const [season, setSeason] = useState<number | 'all'>('all');
  const { data, loading } = useQuery<TournamentResultsData, TournamentResultsVars>(TournamentResultsQuery, {
    variables: {user: characterId ? null : userId, character: characterId || null}, skip: !userId && !characterId, fetchPolicy: 'cache-and-network',
  });
  const all = data?.tournamentResults || [];
  const seasons = Array.from(new Set(all.map((r) => r.season).filter((s): s is number => s != null))).sort((a, b) => b - a);
  const types = Array.from(new Set(all.map((r) => r.type)));
  const results = all.filter((r) => (type == 'all' || r.type == type) && (season == 'all' || r.season == season));

  if(loading && all.length == 0)
    return <LinearProgress />;
  if(all.length == 0)
    return <PageEmpty title="No tournaments yet" text="Finished tournaments and where they placed will show here." />;

  const won = results.filter((r) => r.won).length;
  const podiums = results.filter((r) => r.place <= 3).length;
  const best = results.length > 0 ? Math.min(...results.map((r) => r.place)) : null;
  const bracket = results.filter((r) => r.type != TournamentType.PvE);
  const seriesWon = bracket.reduce((n, r) => n + (r.seriesWon || 0), 0), seriesLost = bracket.reduce((n, r) => n + (r.seriesLost || 0), 0);

  return (
    <div className={classes.root}>
      <div className={classes.filters}>
        <div>
          {types.length > 1 &&
            <Segmented value={type} onChange={setType} options={[{value: 'all' as TypeFilter, label: 'All'}, ...types.map((x) => ({value: x as TypeFilter, label: typeLabel(x)}))]} />
          }
        </div>
        {seasons.length > 0 &&
          <Segmented value={season} onChange={setSeason} options={[{value: 'all' as number | 'all', label: 'All seasons'}, ...seasons.map((s) => ({value: s as number | 'all', label: `Season ${s}`}))]} />
        }
      </div>

      <div className={classes.summary}>
        <div className={`${classes.stat} ${won > 0 ? classes.statHero : ''}`}>
          <div className={classes.statLabel}>Titles</div>
          <div className={classes.statValue}>{won > 0 && <EmojiEvents style={{fontSize: 22, color: TOURNAMENT_ACCENT, verticalAlign: -3, marginRight: 4}} />}{won}</div>
          <div className={classes.statNote}>{podiums} podium{podiums == 1 ? '' : 's'}</div>
        </div>
        <div className={classes.stat}>
          <div className={classes.statLabel}>Played</div>
          <div className={classes.statValue}>{results.length}</div>
          <div className={classes.statNote}>tournament{results.length == 1 ? '' : 's'}</div>
        </div>
        <div className={classes.stat}>
          <div className={classes.statLabel}>Best finish</div>
          <div className={classes.statValue}>{best != null ? ordinal(best) : '–'}</div>
          <div className={classes.statNote}>{results.length > 0 ? `avg ${ordinal(Math.round(results.reduce((n, r) => n + r.place, 0) / results.length))}` : ''}</div>
        </div>
        {bracket.length > 0 &&
          <div className={classes.stat}>
            <div className={classes.statLabel}>Series</div>
            <div className={classes.statValue}>{seriesWon}–{seriesLost}</div>
            <div className={classes.statNote}>{seriesWon + seriesLost > 0 ? `${Math.round(100 * seriesWon / (seriesWon + seriesLost))}% won` : ''}</div>
          </div>
        }
      </div>

      <div className={classes.list}>
        {results.length == 0 && <div className={classes.statNote}>Nothing for this filter.</div>}
        {results.map((r) => <ResultRow key={String(r._id)} result={r} onOpen={() => r.tournament && history.push(`/tournaments/${r.tournament._id}`)} />)}
      </div>
    </div>
  );
}

const ResultRow: React.FC<{result: TournamentResultView, onOpen: () => void}> = ({result: r, onOpen}) => {
  const classes = useStyles();
  const medal = PLACE_STYLE[r.place];
  const color = TYPE_COLORS[r.type] || TOURNAMENT_ACCENT;
  const record = r.type == TournamentType.PvE
    ? {main: `${r.points ?? 0} pts`, sub: r.bestRoundPlace ? `best round ${ordinal(r.bestRoundPlace)}` : ''}
    : {main: `${r.seriesWon ?? 0}–${r.seriesLost ?? 0}`, sub: `games ${r.gamesWon ?? 0}–${r.gamesLost ?? 0}`};
  return (
    <div className={classes.row} onClick={onOpen}>
      <div className={classes.place} style={medal}>
        {r.won ? <EmojiEvents style={{fontSize: 22}} /> : ordinal(r.place)}
        <span className={classes.placeOf}>of {r.entrants}</span>
      </div>
      <div style={{minWidth: 0}}>
        <div className={classes.name}>
          {r.tournament?.name || 'Tournament'}
          {r.substitute && <span className={classes.flag}>Substitute</span>}
          {r.left && <span className={classes.flag}>Left early</span>}
        </div>
        <div className={classes.meta}>
          <span className={classes.typeTag} style={{color, background: `${color}22`}}>{typeLabel(r.type)}</span>
          {[bracketLabel(r.bracket), r.teamName, r.completedAt ? moment(r.completedAt).format('MMM D, YYYY') : null, r.season ? `Season ${r.season}` : null].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className={classes.record}>
        <div className={classes.recordMain}>{record.main}</div>
        <div className={classes.statNote}>{record.sub}</div>
        <div className={classes.icons}>
          {r.characters.map((c) => (
            <Tooltip key={String(c._id)} title={`${c.name} · ${c.class}`}><img src={classImages.getColorIcon(c.class as AocClasses)} alt={c.class} /></Tooltip>
          ))}
        </div>
      </div>
      <ChevronRight className={classes.go} />
    </div>
  );
}
